import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { Divider, Text } from "@src/appV2/redesign/system/ui-components";
import { type ReactNode, useMemo } from "react";

import { WheelColumn } from "../components/WheelTimePicker/WheelColumn";

export interface Duration {
  hours: number;
  minutes: number;
}

interface DurationInputProps {
  value?: Duration;
  onChange: (duration: Duration) => void;
  maxHours?: number;
  minutesStep?: number;
  showFormattedDuration?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  sx?: Record<string, any>;
  /**
   * Useful when DurationInput is nested within a parent that also captures touch events
   * If `true`, `event.stopPropagation()` will be called immediately on touch capture
   * which will prevent the parent from stealing the input's touch events
   */
  preventParentTouchStart?: boolean;
}

// Helper function to format duration into readable text
export function formatDuration(duration: Duration): string {
  const hourText =
    duration.hours > 0 ? `${duration.hours} hour${duration.hours === 1 ? "" : "s"}` : "";

  const minuteText =
    duration.minutes > 0 ? `${duration.minutes} minute${duration.minutes === 1 ? "" : "s"}` : "";

  const separator = hourText && minuteText ? " " : "";

  return `${hourText}${separator}${minuteText}`;
}

export function minutesToDuration(minutes: number): Duration {
  return {
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
  };
}

export function durationToMinutes(duration: Duration): number {
  return duration.hours * 60 + duration.minutes;
}

export function DurationInput(props: DurationInputProps) {
  const {
    value = { hours: 1, minutes: 0 },
    onChange,
    maxHours = 12,
    minutesStep = 1,
    showFormattedDuration = true,
    disabled = false,
    children,
    sx,
    preventParentTouchStart,
  } = props;

  const hourItems = useMemo(
    () =>
      Array.from({ length: maxHours + 1 }, (_, index) => ({
        value: index,
        label: String(index),
        ariaLabel: `${index} hours`,
      })),
    [maxHours]
  );

  const minuteItems = useMemo(
    () =>
      Array.from({ length: Math.ceil(60 / minutesStep) }, (_, index) => {
        const minute = index * minutesStep;
        return {
          value: minute,
          label: String(minute).padStart(2, "0"),
          ariaLabel: `${minute} minutes`,
        };
      }),
    [minutesStep]
  );

  const handleHourSelect = (selectedHour: number | string): void => {
    if (!disabled) {
      onChange({ hours: Number(selectedHour), minutes: value.minutes });
    }
  };

  const handleMinuteSelect = (selectedMinute: number | string): void => {
    if (!disabled) {
      onChange({ hours: value.hours, minutes: Number(selectedMinute) });
    }
  };

  return (
    <Stack
      spacing={4}
      alignItems="center"
      sx={sx}
      onTouchStartCapture={
        preventParentTouchStart
          ? (event) => {
              event.stopPropagation();
            }
          : undefined
      }
    >
      {isDefined(children) && <Box>{children}</Box>}

      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
        <WheelColumn
          name="hours"
          items={hourItems}
          selectedValue={value.hours}
          onSelect={handleHourSelect}
        />
        <Divider flexItem orientation="vertical" />
        <WheelColumn
          name="minutes"
          items={minuteItems}
          selectedValue={value.minutes}
          onSelect={handleMinuteSelect}
        />
      </Box>

      {showFormattedDuration && (
        <Box sx={{ textAlign: "center" }}>
          <Text semibold variant="h6">
            {formatDuration(value)}
          </Text>
        </Box>
      )}
    </Stack>
  );
}
